// src/services/api.js
// API service for Forwardsflow - connects to MySQL backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  // Get current token
  getToken() {
    return localStorage.getItem('authToken');
  }

  // Check if the current session is a demo account (fake token)
  isDemoToken(token) {
    return token && token.startsWith('demo_token_');
  }

  // Make authenticated request
  async request(endpoint, options = {}) {
    const token = this.getToken();

    // Demo accounts use fake tokens that the backend will reject.
    // Skip the network call entirely and let callers fall back to mock data.
    if (this.isDemoToken(token)) {
      throw new Error('Demo mode — API not available');
    }

    const url = `${API_BASE_URL}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (response.status === 401) {
        // Session expired — clear everything and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('forwardsflow_user');
        window.location.href = '/login';
        throw new Error('Session expired');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // GET request
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST request
  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // PATCH request
  async patch(endpoint, body) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // ============================================
  // DASHBOARD
  // ============================================
  async getDashboard() {
    return this.get('/dashboard');
  }

  // ============================================
  // USERS
  // ============================================
  async getUsers() {
    return this.get('/users');
  }

  async getUser(id) {
    return this.get(`/users/${id}`);
  }

  async createUser(userData) {
    return this.post('/users', userData);
  }

  async updateUser(id, userData) {
    return this.patch(`/users/${id}`, userData);
  }

  // ============================================
  // TENANTS
  // ============================================
  async getTenants(type = null) {
    const query = type ? `?type=${type}` : '';
    return this.get(`/tenants${query}`);
  }

  async getTenant(id) {
    return this.get(`/tenants/${id}`);
  }

  async createTenant(tenantData) {
    return this.post('/tenants', tenantData);
  }

  // ============================================
  // ACCOUNTS
  // ============================================
  async getAccounts() {
    return this.get('/accounts');
  }

  async getAccountBalance(id) {
    return this.get(`/accounts/${id}/balance`);
  }

  // ============================================
  // DEPOSIT CALLS
  // ============================================
  async getDepositCalls() {
    return this.get('/deposit-calls');
  }

  async getDepositCall(id) {
    return this.get(`/deposit-calls/${id}`);
  }

  async createDepositCall(callData) {
    return this.post('/deposit-calls', callData);
  }

  // ============================================
  // SUBSCRIPTIONS
  // ============================================
  async getSubscriptions() {
    return this.get('/subscriptions');
  }

  async createSubscription(subData) {
    return this.post('/subscriptions', subData);
  }

  async updateSubscriptionStatus(id, status) {
    return this.patch(`/subscriptions/${id}/status`, { status });
  }

  // ============================================
  // LOANS
  // ============================================
  async getLoans(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/loans${query ? `?${query}` : ''}`);
  }

  async getLoan(id) {
    return this.get(`/loans/${id}`);
  }

  async createLoan(loanData) {
    return this.post('/loans', loanData);
  }

  async approveLoan(id) {
    return this.post(`/loans/${id}/approve`, {});
  }

  async disburseLoan(id) {
    return this.post(`/loans/${id}/disburse`, {});
  }

  async processRepayment(id, amount, reference) {
    return this.post(`/loans/${id}/repayment`, { amount, reference });
  }

  // ============================================
  // INVESTORS
  // ============================================
  async getInvestors() {
    return this.get('/investors');
  }

  async getInvestor(id) {
    return this.get(`/investors/${id}`);
  }

  async createInvestor(investorData) {
    return this.post('/investors', investorData);
  }

  async updateInvestor(id, investorData) {
    return this.patch(`/investors/${id}`, investorData);
  }

  async verifyInvestorKyc(id) {
    return this.post(`/investors/${id}/verify-kyc`, {});
  }

  // ============================================
  // TRANSACTIONS
  // ============================================
  async getTransactions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/transactions${query ? `?${query}` : ''}`);
  }

  async getTransaction(id) {
    return this.get(`/transactions/${id}`);
  }

  async getTransactionSummary() {
    return this.get('/transactions/stats/summary');
  }

  async getTransactionsByMonth(months = 6) {
    return this.get(`/transactions/stats/by-month?months=${months}`);
  }
  // ============================================================
  // RATINGS
  // ============================================================
  async getBankRatings() {
    return this.get('/ratings/banks');
  }

  async getBankRating(tenantId) {
    return this.get(`/ratings/banks/${tenantId}`);
  }

  async getBankRatingHistory(tenantId) {
    return this.get(`/ratings/banks/${tenantId}/history`);
  }

  async getSovereignRatings(region = null) {
    const query = region ? `?region=${region}` : '';
    return this.get(`/ratings/sovereign${query}`);
  }

  async getCounterpartyRatings(bankTenantId = null) {
    const query = bankTenantId ? `?bankTenantId=${bankTenantId}` : '';
    return this.get(`/ratings/counterparties${query}`);
  }

  async getCounterpartyRating(counterpartyId) {
    return this.get(`/ratings/counterparties/${counterpartyId}`);
  }

  async getRatingSegments() {
    return this.get('/ratings/segments');
  }

  async submitLaunchSignup(data) {
    const token = this.getToken();
    if (this.isDemoToken(token) || process.env.REACT_APP_USE_MOCK === 'true') {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            message: 'Signup recorded',
            channels: data.channels,
            double_optin_sent: true
          });
        }, 1000);
      });
    }

    try {
      return await this.post('/signups/launch', data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('API error, falling back to simulated signup:', err);
        return {
          success: true,
          message: 'Signup recorded (Simulated Fallback)',
          channels: data.channels,
          double_optin_sent: true
        };
      }
      throw err;
    }
  }

  async getCoreBankingSystems() {
    const token = this.getToken();
    if (this.isDemoToken(token) || process.env.REACT_APP_USE_MOCK === 'true') {
      return MOCK_CORE_BANKING_SYSTEMS;
    }
    try {
      return await this.get('/core-banking-systems');
    } catch (err) {
      console.warn('API error fetching core banking systems, using mock fallback:', err);
      return MOCK_CORE_BANKING_SYSTEMS;
    }
  }

  // ============================================
  // PORTFOLIO
  // ============================================
  async getPortfolioPositions(entityFilter = null) {
    const query = entityFilter ? `?entity=${entityFilter}` : '';
    return this.get(`/portfolio/positions${query}`);
  }

  async getPortfolioPerformance(period = 'ITD') {
    return this.get(`/portfolio/performance?period=${period}`);
  }

  async getPortfolioCashFlows() {
    return this.get('/portfolio/cash-flows');
  }

  async getPortfolioRisk() {
    return this.get('/portfolio/risk');
  }

  async getPortfolioFxHedges() {
    return this.get('/portfolio/fx-hedges');
  }

  async getPortfolioBenchmarks() {
    return this.get('/portfolio/benchmarks');
  }

  async getPortfolioGovernance() {
    return this.get('/portfolio/governance');
  }

  // ============================================
  // WAITLIST (Public — no auth token)
  // ============================================
  async submitWaitlistSubscription(data) {
    const token = this.getToken();
    if (this.isDemoToken(token) || process.env.REACT_APP_USE_MOCK === 'true') {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, message: 'Subscribed', subscriberId: 'demo-' + Date.now() });
        }, 800);
      });
    }

    try {
      const url = `${API_BASE_URL}/waitlist/subscribe`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Subscription failed');
      return result;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Waitlist API error, falling back to simulated:', err);
        return { success: true, message: 'Subscribed (Simulated)', subscriberId: 'fallback-' + Date.now() };
      }
      throw err;
    }
  }

  async updateWaitlistPhone(id, data) {
    const token = this.getToken();
    if (this.isDemoToken(token) || process.env.REACT_APP_USE_MOCK === 'true') {
      return new Promise((resolve) => {
        setTimeout(() => resolve({ success: true, message: 'Phone updated' }), 500);
      });
    }

    try {
      const url = `${API_BASE_URL}/waitlist/subscribe/${id}/phone`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Phone update failed');
      return result;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Waitlist phone API error, falling back:', err);
        return { success: true, message: 'Phone updated (Simulated)' };
      }
      throw err;
    }
  }

  async submitDemoRegistration(data) {
    // Always call the real backend — demo registration is a public lead-capture
    // endpoint that must persist to the database and trigger confirmation emails,
    // regardless of whether the user has a demo token in localStorage.
    const url = `${API_BASE_URL}/waitlist/demo-register`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Demo registration failed');
    return result;
  }

  async submitReferral({ referrer_id, referral_email }) {
    const url = `${API_BASE_URL}/waitlist/demo-refer`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referrer_id, referral_email }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Referral failed');
    return result;
  }

  async verifyDemoToken(token) {
    try {
      const url = `${API_BASE_URL}/waitlist/demo-verify?token=${encodeURIComponent(token)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Demo verification failed');
      return result;
    } catch (err) {
      console.warn('Demo verify API error:', err);
      return { success: false };
    }
  }

  // ============================================
  // WAITLIST (Admin — authenticated)
  // ============================================
  async getWaitlistSubscribers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/waitlist/subscribers${query ? `?${query}` : ''}`);
  }

  async getWaitlistAnalytics() {
    return this.get('/waitlist/analytics');
  }

  async getWaitlistGrowth(interval = 'day') {
    return this.get(`/waitlist/analytics/growth?interval=${interval}`);
  }

  async getWaitlistCohorts() {
    return this.get('/waitlist/analytics/cohorts');
  }

  async getWaitlistEngagement() {
    return this.get('/waitlist/analytics/engagement');
  }

  async getWaitlistSurveyThemes() {
    return this.get('/waitlist/analytics/survey-themes');
  }

  async inviteWaitlistSubscriber(id) {
    return this.post(`/waitlist/subscribers/${id}/invite`, {});
  }

  async batchInviteWaitlist(data) {
    return this.post('/waitlist/batch-invite', data);
  }

  async updateWaitlistSubscriber(id, data) {
    return this.patch(`/waitlist/subscribers/${id}`, data);
  }

  async getWaitlistFunnel() {
    return this.get('/waitlist/funnel');
  }

  async validateWaitlistEmails() {
    return this.post('/waitlist/validate-emails', {});
  }

  // ============================================
  // SIMULATOR
  // ============================================
  async getSimulatorRateData() {
    return this.get('/simulator/rate-data');
  }

  async getSimulatorAuctions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/simulator/auctions${query ? `?${query}` : ''}`);
  }

  async getSimulatorFxRates(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/simulator/fx-rates${query ? `?${query}` : ''}`);
  }

  async getSimulatorConfig() {
    return this.get('/simulator/config');
  }

  async runInvestorSimulation(params) {
    return this.post('/simulator/investor/run', params);
  }

  async getInvestorSimRuns(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/simulator/investor/runs${query ? `?${query}` : ''}`);
  }

  async getInvestorSimRun(id) {
    return this.get(`/simulator/investor/runs/${id}`);
  }

  // Live investor simulation
  async createLiveInvestorRun(params) {
    return this.post('/simulator/investor/live', params);
  }

  async getLiveInvestorRuns() {
    return this.get('/simulator/investor/live');
  }

  async getLiveInvestorRun(id) {
    return this.get(`/simulator/investor/live/${id}`);
  }

  async cancelLiveInvestorRun(id) {
    return this.post(`/simulator/investor/live/${id}/cancel`);
  }

  async getLiveHistoricalComparison(id) {
    return this.get(`/simulator/investor/live/${id}/historical-comparison`);
  }

  // Bank simulation
  async runBankSimulation(params) {
    return this.post('/simulator/bank/run', params);
  }

  async getBankSimRuns() {
    return this.get('/simulator/bank/runs');
  }

  async getBankSimRun(id) {
    return this.get(`/simulator/bank/runs/${id}`);
  }

  async saveBankPricingScenario(params) {
    return this.post('/simulator/bank/pricing-scenarios', params);
  }

  async getBankPricingScenarios() {
    return this.get('/simulator/bank/pricing-scenarios');
  }

  // ============================================
  // SIM ADMIN MIGRATIONS
  // ============================================
  async runSimMigration(filename) {
    return this.post('/sim-admin/migrations/run', { filename });
  }

  // LIVE EXPERIMENT
  // ============================================
  async createLiveExperiment(data) {
    return this.post('/sim-admin/live-experiments', data);
  }

  async getLiveExperiments() {
    return this.get('/sim-admin/live-experiments');
  }

  async getLiveExperiment(id) {
    return this.get(`/sim-admin/live-experiments/${id}`);
  }

  async getLiveExperimentMarks(id, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/sim-admin/live-experiments/${id}/marks${query ? `?${query}` : ''}`);
  }

  async updateLiveExperimentAssumptions(id, data) {
    return this.patch(`/sim-admin/live-experiments/${id}/assumptions`, data);
  }

  async fetchResultsNow(id) {
    return this.post(`/sim-admin/live-experiments/${id}/fetch-results-now`, {});
  }

  async dryRunExperiment(id) {
    return this.post(`/sim-admin/live-experiments/${id}/dry-run`, {});
  }

  async closeExperimentNow(id) {
    return this.post(`/sim-admin/live-experiments/${id}/close-now`, {});
  }

  async getLiveExperimentExport(id) {
    // Returns blob for download
    const token = this.getToken();
    const url = `${API_BASE_URL}/sim-admin/live-experiments/${id}/export.xlsx`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  async getPresignedCbkDoc(id) {
    return this.get(`/sim-admin/cbk-documents/${id}/presigned`);
  }

  async getCountdown() {
    return this.get('/sim-admin/countdown');
  }

  async getLiveExperimentReports(id) {
    return this.get(`/sim-admin/live-experiments/${id}/reports`);
  }

  async previewReport(id) {
    const token = this.getToken();
    const url = `${API_BASE_URL}/sim-admin/live-experiments/${id}/reports/preview.pdf`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Preview failed');
    return response.blob();
  }

  async sendReportNow(id) {
    return this.post(`/sim-admin/live-experiments/${id}/reports/send-now`, {});
  }

  async getReportRecipients() {
    return this.get('/sim-admin/report-recipients');
  }

  async updateReportRecipients(data) {
    return this.request('/sim-admin/report-recipients', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async addCoreBankingSystem(name) {
    const token = this.getToken();
    if (this.isDemoToken(token) || process.env.REACT_APP_USE_MOCK === 'true') {
      const nextId = MOCK_CORE_BANKING_SYSTEMS.length + 1;
      const newSystem = { id: nextId, name };
      MOCK_CORE_BANKING_SYSTEMS.push(newSystem);
      return { success: true, id: nextId, name };
    }
    try {
      return await this.post('/core-banking-systems', { name });
    } catch (err) {
      console.warn('API error adding core banking system, using mock fallback:', err);
      const nextId = MOCK_CORE_BANKING_SYSTEMS.length + 1;
      const newSystem = { id: nextId, name };
      MOCK_CORE_BANKING_SYSTEMS.push(newSystem);
      return { success: true, id: nextId, name };
    }
  }

  // ─── Secondary Market ────────────────────────────────────────────────
  async getSecondaryListings(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.get(`/secondary-market/listings${qs ? `?${qs}` : ''}`);
  }

  async createListing(data) {
    return this.post('/secondary-market/listings', data);
  }

  async placeBid(listingId, data) {
    return this.post(`/secondary-market/listings/${listingId}/bids`, data);
  }

  async acceptBid(bidId) {
    return this.patch(`/secondary-market/bids/${bidId}`, { status: 'accepted' });
  }

  async rejectBid(bidId) {
    return this.patch(`/secondary-market/bids/${bidId}`, { status: 'rejected' });
  }

}

const MOCK_CORE_BANKING_SYSTEMS = [
  { id: 1, name: 'Atlas / eBBS (Standard Chartered proprietary)' },
  { id: 2, name: 'Bankmaster (Finastra / formerly Misys)' },
  { id: 3, name: 'Co-Bank+ (Tecnostac)' },
  { id: 4, name: 'CoopMIS' },
  { id: 5, name: 'Craft Silicon' },
  { id: 6, name: 'Dynamics 365 Business Central' },
  { id: 7, name: 'Finacle (Infosys)' },
  { id: 8, name: 'Fusion Banking Essence (Finastra)' },
  { id: 9, name: 'Fusion Essence' },
  { id: 10, name: 'Intel Solutions' },
  { id: 11, name: 'Intellect Design Arena eMACH.ai' },
  { id: 12, name: 'Intrasoft Profits' },
  { id: 13, name: 'Murong Digital Banking Core System' },
  { id: 14, name: 'Neptune Rubikon' },
  { id: 15, name: 'Olympic Core Banking' },
  { id: 16, name: 'Omni Enterprise' },
  { id: 17, name: 'Oracle FLEXCUBE' },
  { id: 18, name: 'Path Solutions iMAL' },
  { id: 19, name: 'Shared Core Banking Platform' },
  { id: 20, name: 'Sopra Banking Software' },
  { id: 21, name: 'Sunline' },
  { id: 22, name: 'TCS BaNCS' },
  { id: 23, name: 'Temenos T24' },
  { id: 24, name: 'Temenos Transact' }
];

// Export singleton instance
const api = new ApiService();
export default api;

// Also export the class for testing
export { ApiService };
